import { tradePromoOptimizationUrls } from 'config/urls';
import { UserContext } from 'contexts/ConfigContext';
import { TPODataContext } from 'contexts/TPOContext';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    downloadCsv,
    formatNumberWithCommas,
    generateErrorMsg,
    getConfig,
    removePropertyByKeyName,
    sendNotification,
    transformFiltersV2,
} from 'utils/helperMethods';
import KPISelector from './AddEventModal/PromoSections/KPISelector';
import { Button, Dropdown, Switch } from 'antd';
import { ProjectOutlined, EllipsisOutlined, ExportOutlined } from '@ant-design/icons';
import { StyledEllipsisButton } from './tpoStyledComponents';
import InfiniteScroller from '../InfiniteScroller';
import { CustomTable } from 'components/template/PricingSimulator/PricingSimulatorIndexTableWrapper';
import { useFillHeightToFitScreen } from 'hooks/general/useFillHeightToFitScreen';
import { useFetchToolsLibraryData } from 'hooks/tradePromoOptimization/plans/useFetchToolsLibraryData';
import { getPromoLibraryTableColumns } from 'utils/getTradePromoOptimizationTableColumns';
import Footer from '../PricingSimulator/Footer';
import TableErrorBox from '../ErrorBox/TableErrorBox';
import EditToolsMetricsModal, { DataType } from './Modals/EditToolsMetricsModal';
import ResetPromoLibraryModal from './Modals/ResetPromoLibraryModal';
import { queryClient } from 'services/reactQueryService';
import { PromoLibraryKpiConfigType } from 'types/tradePromoOptimization';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import { ErrorMessages, tpoNotificationMessages } from 'utils/Enums';
import { transformFilterObjectWithDimensionsInfo } from 'utils/getPricingSimulatorTableColumns';
import NewGlobalFilter from '../common/NewGlobalFilter/NewGlobalFilter';
import { ToolTabKeys } from 'components/organism/TradePromoOptimization/Tools';
import FilterRibbon from '../PricingSimulator/FilterRibbon/FilterRibbon';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { buildResetPayload, formatKpiOptionsForReset, validateSingleCustomerSelection } from './tpoHelperMethods';
import { trackEventMixpanel } from 'utils/mixpanel';
import { TPO_TOOLS_CELL_EDITED, TPO_TOOLS_COLUMN_EDITED } from 'utils/mixpanelEventNames';
import ValidateLibraryDrawer from './Modals/ValidateLibraryDrawer';

export const initialEditModalState: { open: boolean; kpiData: DataType } = {
    open: false,
    kpiData: { kpiObject: {} },
};

export interface PromoLibraryContainerProps {
    libraryGroup?: ToolTabKeys;
    tableId?: string;
    queryKeyPrefix?: string;
    //Suffix for KPI config URL (e.g., 'promo_library', 'mechanic_library', 'product_constraint')
    kpiConfigUrlSuffix?: string;
    attributeTypesConfigKey?: string;
    exportFileName?: string;
    tableDataUrl?: string;
    updateUrl?: string;
    resetLabel?: string;
}

const PromoLibraryContainer: React.FC<PromoLibraryContainerProps> = ({
    libraryGroup = ToolTabKeys.PromoLibrary,
    tableId = 'promo-library-table-id',
    queryKeyPrefix = 'tpo-promo-library',
    kpiConfigUrlSuffix = 'promo_library',
    attributeTypesConfigKey = 'promoLibraryAttributeTypes',
    exportFileName = 'promo_library.csv',
    tableDataUrl = tradePromoOptimizationUrls.getPromoLibraryTableDataUrl,
    updateUrl = tradePromoOptimizationUrls.updatePromoLibraryUrl,
    resetLabel = 'Merch Core Promos',
}) => {
    const {
        appConfig,
        exportPromoLibraryOptions: { exportToolsData },
    } = useContext(UserContext);
    const tpoConfig = getConfig('tradePromoOptimization', appConfig);
    const promoLibraryExportThreshold = tpoConfig?.promoLibraryExportThreshold;
    const {
        availableFilters,
        allCheckedAttribute,
        setAllCheckedAttribute,
        allSelectedValueForAllAttribute,
        setAllSelectedValueForAllAttribute,
        setApplyFilterObject,
        applyFilterObject,
        resetFilterObject,
        uiDimensionConfigData,
        filterUIDimensionsLoading,
        showChanges,
        setShowChanges,
        isToolsViewAbsolute,
        setIsToolsViewAbsolute,
    } = useContext(TPODataContext);

    const [openEditModal, setOpenEditModal] = useState({ ...initialEditModalState });
    const [openResetModal, setOpenResetModal] = useState(false);
    const [openValidateDrawer, setOpenValidateDrawer] = useState(false);
    const [toolsKpiConfigData, setToolsKpiConfigData] = useState<null | PromoLibraryKpiConfigType>(null);
    const [toolsTableVariable, setToolsTableVariable] = useState<any>({
        limit: tpoConfig?.tableLimit,
        offset: 0,
        kpis: [],
        library_group: libraryGroup,
        filters: {},
    });
    const [isToolsKpiFilterVisible, setIsToolsKpiFilterVisible] = useState(false);
    const [toolsKpiFilterDropdownValues, setToolsKpiDropdownValues] = useState<any[]>([]);
    const [selectedFilter, setSelectedFilter] = useState<any>(null);
    const [tempApplyFilterObject, setTempApplyFilterObject] = useState<any>({});

    const attributeTypes = tpoConfig?.[attributeTypesConfigKey];

    const tableHeight = useRef<number | null>(0);
    const tableScrollPositionRef = useRef<number>(0);
    const optionDropdownItems = useMemo(() => {
        const items = [
            {
                key: `reset_${libraryGroup}`,
                label: <div className="font-xs cursor-pointer">Reset {resetLabel}</div>,
            },
            {
                key: 'validate_library',
                label: <div className="font-xs cursor-pointer">Validate Library</div>,
            },
        ];

        return items;
    }, [resetLabel, libraryGroup]);

    const kpiOptionsForReset = useMemo(
        () => formatKpiOptionsForReset(toolsKpiFilterDropdownValues),
        [toolsKpiFilterDropdownValues],
    );

    //this hook with give height to fill the screen height
    tableHeight.current = useFillHeightToFitScreen({
        id: tableId,
        height: tableHeight.current,
        buffer: 110, //footer
    });

    const { mutate: updateToolsLibrary, loading: updateToolsLibraryLoading } = useCommonMutationHandler({
        url: updateUrl,
        method: 'post',
    });

    const { mutate: resetPromoLibrary, loading: resetPromoLibraryLoading } = useCommonMutationHandler({
        url: tradePromoOptimizationUrls.resetPromoLibraryUrl,
        method: 'post',
    });

    //GET KPI CONFIG DATA FROM BACKEND
    const { loading: toolsKpiConfigLoading } = useCommonApiHandler({
        method: 'get',
        queryConfig: {},
        queryKey: [`${queryKeyPrefix}-kpi-config`],
        url: `${tradePromoOptimizationUrls.getPromoLibraryKpiUrl}/${kpiConfigUrlSuffix}`,
        variables: {},
        parserFunction(data: any) {
            const res: PromoLibraryKpiConfigType = {};
            data?.forEach((item: any) => {
                res[item?.kpi_key] = {
                    pre: item?.pre,
                    post: item?.post,
                    label: item?.kpi_name,
                    allowNegative: item?.allow_negative,
                    isEditable: item?.is_editable,
                    maxValue: item?.max_value,
                    minValue: item?.min_value,
                    type: item?.kpi_type,
                    storageType: item?.storage_type,
                    allowedDecimals: item?.allowed_decimals,
                    allowedValues: item?.allowed_values,
                };
            });
            setToolsKpiConfigData(res);
            return {};
        },
    });

    //library data
    const {
        loading: toolsTableDataLoading,
        error: toolsTableDataError,
        data: toolsTableData,
        count: toolsTableDataCount,
    } = useFetchToolsLibraryData({
        method: 'post',
        queryConfig: {
            enabled:
                Boolean(
                    toolsTableVariable?.kpis?.length > 0 &&
                        Object.keys(toolsTableVariable?.filters)?.length > 0 &&
                        uiDimensionConfigData?.filterKeys?.length > 0,
                ) && !toolsKpiConfigLoading,
        },
        url: tableDataUrl,
        queryKey: ['tpo-promo-library-data', `${queryKeyPrefix}-table-data`, toolsTableVariable],
        variables: { ...(toolsTableVariable || {}) },
        kpiConfigData: toolsKpiConfigData,
        attributeTypes: attributeTypes,
    });

    useEffect(() => {
        const valueList: any[] = [];
        const updatedList = Object.keys(toolsKpiConfigData ?? {})?.map((configKey) => {
            valueList.push({
                key: configKey,
                label: toolsKpiConfigData?.[configKey]['label'],
                storage_type: toolsKpiConfigData?.[configKey]['storageType'],
                is_editable: toolsKpiConfigData?.[configKey]['isEditable'],
                type: toolsKpiConfigData?.[configKey]['type'],
                allowed_decimals: toolsKpiConfigData?.[configKey]['allowedDecimals'],
            });
            return {
                ...toolsKpiConfigData?.[configKey],
                label: toolsKpiConfigData?.[configKey]['label'],
                value: configKey,
                isSelected: true,
                pre: toolsKpiConfigData?.[configKey]['pre'],
                post: toolsKpiConfigData?.[configKey]['post'],
            };
        });
        //reset table scroll position to top on sorting
        tableScrollPositionRef.current = 0;
        setToolsTableVariable((prev: any) => {
            return {
                ...prev,
                offset: 0,
                kpis: valueList,
                sort_by: attributeTypes?.[0]?.sortKey,
                sort_order: 'asc',
                library_group: libraryGroup,
            };
        });
        setToolsKpiDropdownValues(updatedList);
    }, [toolsKpiConfigData, attributeTypes, libraryGroup]);

    useEffect(() => {
        //reset table scroll position to top on sorting
        tableScrollPositionRef.current = 0;
        setToolsTableVariable((prev: any) => {
            return { ...prev, limit: tpoConfig?.tableLimit, offset: 0, kpis: [] };
        });
    }, [libraryGroup]);

    useEffect(() => {
        //reset table scroll position to top on sorting
        tableScrollPositionRef.current = 0;
        const updatedFilters = transformFiltersV2(applyFilterObject, uiDimensionConfigData?.filterKeys ?? []);
        setToolsTableVariable((prev: any) => ({ ...prev, filters: updatedFilters, offset: 0 }));
    }, [applyFilterObject, uiDimensionConfigData?.filterKeys]);

    function toolsInfiniteScrollOnNext(loading: boolean) {
        if (loading) return;
        //reset table scroll position to top on sorting
        tableScrollPositionRef.current = 0;
        const newOffset = toolsTableVariable.offset + toolsTableVariable.limit;
        setToolsTableVariable({ ...toolsTableVariable, offset: newOffset });
    }

    function onEdit(
        from: 'cell' | 'column',
        kpiObject: PromoLibraryKpiConfigType,
        value?: any,
        rowId?: number,
        rowLabel?: string,
    ) {
        setOpenEditModal({
            kpiData: {
                kpiObject,
                value: from === 'column' ? undefined : value,
                rowId: from === 'column' ? undefined : rowId,
                rowLabel: from === 'column' ? undefined : rowLabel,
            },
            open: true,
        });
    }

    const columns = useMemo(() => {
        if (!toolsTableData) return [];
        const tempColumns = toolsKpiFilterDropdownValues
            ?.filter((item) => item.isSelected)
            ?.map((item) => {
                return {
                    label: item?.label,
                    key: item?.label,
                    kpiInfo: item,
                    sortKey: item?.storageType === 'range' ? false : item?.value,
                };
            });
        const memoisedColumns = getPromoLibraryTableColumns(
            attributeTypes,
            tempColumns,
            onEdit,
            {
                sortBy: toolsTableVariable?.sort_by,
                sortOrder: toolsTableVariable?.sort_order === 'asc' ? 'ascend' : 'descend',
            },
            showChanges,
            isToolsViewAbsolute,
        );
        return memoisedColumns;
    }, [
        toolsTableData,
        showChanges,
        isToolsViewAbsolute,
        // toolsKpiFilterDropdownValues,
        // attributeTypes,
        // toolsTableVariable?.sort_by,
        // toolsTableVariable?.sort_order,
    ]);

    const toolsCommonLoading = filterUIDimensionsLoading || toolsTableDataLoading || toolsKpiConfigLoading;

    const toolsIsErrorStatus = toolsTableDataError;
    const toolsFooterFirstText = `${
        'Showing ' +
        Number(toolsTableData?.length ?? 0)?.toLocaleString() +
        ' of ' +
        formatNumberWithCommas(toolsTableDataCount, 0) +
        ' records'
    }`;
    const footerSecondText = '';
    const footerThirdText = '';
    const footerFourthText = '';

    const onFilterSaveClickHandler = (resultObj: any) => {
        setAllCheckedAttribute(resultObj.allCheckedAttribute);
        setAllSelectedValueForAllAttribute(resultObj.allSelectedValueForAllAttribute);
        setApplyFilterObject(resultObj.applyFilterObject);
    };

    const commonVariables = useMemo(() => {
        if (selectedFilter?.value && uiDimensionConfigData?.filterKeys) {
            const updatedFilters = transformFiltersV2(
                tempApplyFilterObject,
                uiDimensionConfigData?.filterKeys,
                selectedFilter.value,
            );

            return {
                attribute_type: selectedFilter.value,
                limit: 50,
                filters: updatedFilters,
                library_group: libraryGroup,
            };
        }
    }, [selectedFilter]);

    function exportToolsLibrary() {
        if (toolsTableDataCount > promoLibraryExportThreshold) {
            return sendNotification(
                'error',
                `The data exceeds the export limit of ${formatNumberWithCommas(
                    promoLibraryExportThreshold,
                    0,
                )} records. Please apply more filters to reduce the data before proceeding with the export.
            `,
                10,
                500,
            );
        }
        sendNotification('info', tpoNotificationMessages.PLAN_EXPORT_QUEUE, 7, 500);
        const variables = {
            ...toolsTableVariable,
            limit: isNaN(toolsTableDataCount) ? promoLibraryExportThreshold : toolsTableDataCount,
            default_headers: attributeTypes?.map((item: any) => ({ key: item?.key, label: item?.label })),
        };
        exportToolsData(variables, exportFileName);
    }

    const findScrollbarElement = () => {
        const container = document.querySelector('#tools-table-container');
        const scrollEl = container?.querySelector('.infinite-scroll-component');
        return scrollEl;
    };

    //assign table scroll position to the ref when table data is updated
    useEffect(() => {
        if (toolsTableData?.length) {
            const scrollEl = findScrollbarElement();
            if (scrollEl && tableScrollPositionRef.current !== 0) {
                scrollEl.scrollTop = tableScrollPositionRef.current;
            }
        }
    }, [toolsTableData]);

    return (
        <div id="tools-table-container" className="relative">
            <div className="flex justify-between mt-12 mb-2 items-center">
                <div className="flex items-center gap-2"></div>

                <div className="flex justify-end gap-2 items-center">
                    <div className="flex items-center gap-2">
                        <p className="font-xs text-fontGreyAlt2">Show changes</p>
                        <Switch
                            checked={showChanges}
                            onChange={(val) => {
                                setShowChanges(val);
                            }}
                        />
                    </div>
                    {/* <Legend className="legend-size-square" data={legendItems} /> */}
                    <NewGlobalFilter
                        availableFilters={availableFilters}
                        allCheckedAttribute={allCheckedAttribute}
                        allSelectedValueForAllAttribute={allSelectedValueForAllAttribute}
                        applyFilterObject={applyFilterObject}
                        resetFilterObject={resetFilterObject}
                        onSubmitHandler={onFilterSaveClickHandler}
                        url={tradePromoOptimizationUrls.getAttributeNames}
                        selectedFilter={selectedFilter}
                        setSelectedFilter={setSelectedFilter}
                        tempApplyFilterObject={tempApplyFilterObject}
                        setTempApplyFilterObject={setTempApplyFilterObject}
                        apiVariables={commonVariables}
                        filterDimensionInfo={uiDimensionConfigData?.filterDimensionInfo}
                        showAttributePanelToLeft
                    />
                    <KPISelector
                        buttonComponent={
                            <Button
                                onClick={() => {
                                    setIsToolsKpiFilterVisible(!isToolsKpiFilterVisible);
                                }}
                                icon={<ProjectOutlined style={{ position: 'relative', top: -3 }} />}
                                style={{ borderRadius: 4, margin: 0 }}
                                type="default"
                            >
                                KPI
                            </Button>
                        }
                        isVisible={isToolsKpiFilterVisible}
                        setIsVisible={setIsToolsKpiFilterVisible}
                        kpiList={toolsKpiFilterDropdownValues}
                        onSubmit={(values) => {
                            const updatedList = toolsKpiFilterDropdownValues?.map((item) => {
                                return {
                                    ...item,
                                    isSelected: values.includes(item?.value),
                                };
                            });
                            const updatedValues = toolsKpiFilterDropdownValues
                                ?.filter((kpiItem) => {
                                    return values.includes(kpiItem?.value);
                                })
                                .map((item) => ({
                                    key: item?.value,
                                    is_editable: item?.isEditable,
                                    storage_type: item?.storageType,
                                    type: item?.type,
                                    allowed_decimals: item?.allowedDecimals,
                                }));
                            //reset table scroll position to top on sorting
                            tableScrollPositionRef.current = 0;
                            setToolsTableVariable({
                                ...toolsTableVariable,
                                offset: 0,
                                kpis: updatedValues,
                                sort_by:
                                    updatedValues?.find((item) => item?.key === toolsTableVariable?.sort_by)?.key ??
                                    attributeTypes?.[0]?.sortKey,
                                sort_order: toolsTableVariable?.sort_order ?? 'asc',
                            });
                            setToolsKpiDropdownValues(updatedList);
                        }}
                        variableName={'KPI'}
                        hideSearch={true}
                    />

                    <Button
                        onClick={exportToolsLibrary}
                        disabled={toolsCommonLoading || toolsTableData?.length === 0}
                        style={{ borderRadius: 6 }}
                        type="default"
                        icon={<ExportOutlined style={{ position: 'relative', top: -3 }} />}
                    >
                        Export
                    </Button>

                    <Dropdown
                        trigger={['click']}
                        menu={{
                            style: { borderRadius: 8, padding: 4 },
                            items: optionDropdownItems,
                            onClick: (val) => {
                                if (val.key === `reset_${libraryGroup}`) {
                                    if (!validateSingleCustomerSelection(toolsTableVariable?.filters)) return;
                                    setOpenResetModal(true);
                                } else if (val.key === 'validate_library') {
                                    setOpenValidateDrawer(true);
                                }
                            },
                        }}
                    >
                        <StyledEllipsisButton type="text" icon={<EllipsisOutlined />} />
                    </Dropdown>
                </div>
            </div>
            {/*3.  Filter Ribbon  */}
            <div className="my-3">
                <FilterRibbon
                    allCheckedAttribute={allCheckedAttribute}
                    allSelectedValueForAllAttribute={allSelectedValueForAllAttribute}
                    applyFilterObject={applyFilterObject}
                    resetFilterObject={resetFilterObject}
                    onSubmitHandler={(obj: any) => {
                        setAllCheckedAttribute(obj.allCheckedAttribute);
                        setAllSelectedValueForAllAttribute(obj.allSelectedValueForAllAttribute);
                        setApplyFilterObject(obj.applyFilterObject);
                    }}
                />
            </div>
            <InfiniteScroller
                data={toolsTableData}
                loading={toolsCommonLoading}
                hasMore={toolsTableData?.length < (toolsTableDataCount || 0) || toolsCommonLoading}
                height={
                    toolsTableData?.length && !toolsTableDataError
                        ? tableHeight.current ?? undefined
                        : isNaN(Number(tableHeight.current))
                        ? undefined
                        : Number(tableHeight.current) - 1
                }
                onNext={() => toolsInfiniteScrollOnNext(toolsCommonLoading)}
            >
                <CustomTable
                    bordered
                    onChange={(pagination, filter, sort: any, extra) => {
                        if (sort?.order) {
                            //reset table scroll position to top on sorting
                            tableScrollPositionRef.current = 0;
                            const sortMapping: any = {
                                descend: 'desc',
                                ascend: 'asc',
                            };

                            setToolsTableVariable({
                                ...toolsTableVariable,
                                sort_by: sort?.column?.sortKey,
                                sort_order: sortMapping?.[sort?.order] || 'asc',
                                offset: 0,
                            });
                        }
                    }}
                    sticky={{
                        getContainer: () =>
                            document.getElementsByClassName('infinite-scroll-component')?.[0] as HTMLElement,
                    }} //adding x axis scrollbar on table
                    id={tableId}
                    columns={columns}
                    scroll={{ x: '100%' }}
                    showSorterTooltip={false}
                    dataSource={toolsTableData}
                    pagination={false}
                    style={{
                        opacity: toolsCommonLoading ? '0.3' : 1,
                        pointerEvents: toolsCommonLoading ? 'none' : 'auto',
                    }}
                    locale={{
                        emptyText: toolsCommonLoading ? (
                            <></>
                        ) : toolsTableDataError ? (
                            <TableErrorBox error={toolsTableDataError} height={tableHeight.current} />
                        ) : (
                            ''
                        ),
                    }}
                />
            </InfiniteScroller>
            <Footer
                type={toolsIsErrorStatus ? 'error' : 'default'}
                items={[toolsFooterFirstText, footerSecondText, footerThirdText, footerFourthText]}
            />
            <EditToolsMetricsModal
                updatePromoLibraryLoading={updateToolsLibraryLoading}
                data={openEditModal.kpiData}
                open={openEditModal.open}
                onSubmit={(payload: any) => {
                    // Validate customer selection before proceeding
                    if (!validateSingleCustomerSelection(toolsTableVariable?.filters)) return;

                    const updatedPayload = {
                        ...(payload ?? {}),
                        library_group:
                            libraryGroup === ToolTabKeys.PromoLibraryView ? ToolTabKeys.PromoLibrary : libraryGroup,
                        filters: toolsTableVariable.filters,
                    };
                    trackEventMixpanel(payload?.row_id ? TPO_TOOLS_CELL_EDITED : TPO_TOOLS_COLUMN_EDITED, {
                        ...(updatedPayload ?? {}),
                    });
                    //first clear the variables so that on success fresh api can be triggered
                    updateToolsLibrary(updatedPayload, {
                        onSuccess: (data: any) => {
                            // assign table scroll position to the ref
                            const tableContainer = findScrollbarElement();
                            if (toolsTableVariable.offset !== 0) {
                                sendNotification('info', tpoNotificationMessages.EDIT_TOOLS_SCROLL_INFO);
                            } else if (tableContainer) {
                                tableScrollPositionRef.current = tableContainer.scrollTop;
                            }
                            setToolsTableVariable({ ...toolsTableVariable, offset: 0 });
                            sendNotification('success', tpoNotificationMessages.EDIT_PROMO_LIBRARY_SUCCESS);
                            //on success, REMOVE THE CACHE

                            queryClient.removeQueries({
                                exact: false,
                                queryKey: ['tpo-promo-library-data', `${queryKeyPrefix}-table-data`],
                            });
                            setOpenEditModal({ ...initialEditModalState });
                        },
                        onError: (error: any) => {
                            const errorMsg = generateErrorMsg(
                                String(error?.response?.data?.detail || ''),
                                ErrorMessages.GENERALIZED_ERROR_MSG,
                            );
                            //show error message
                            sendNotification('error', errorMsg);
                            setOpenEditModal({ ...initialEditModalState });
                        },
                    });
                }}
                onCancel={() => {
                    setOpenEditModal({ ...initialEditModalState });
                }}
            />
            <ResetPromoLibraryModal
                open={openResetModal}
                onCancel={() => setOpenResetModal(false)}
                onSubmit={(payload) => {
                    const resetPayload = buildResetPayload(
                        libraryGroup,
                        payload.kpiIds || [],
                        toolsKpiConfigData,
                        toolsTableVariable?.filters,
                        payload.resetOption,
                    );

                    resetPromoLibrary(resetPayload, {
                        onSuccess: (data: any) => {
                            sendNotification('success', tpoNotificationMessages.RESET_PROMO_LIBRARY_SUCCESS);
                            // Invalidate and refetch table data
                            queryClient.removeQueries({
                                exact: false,
                                queryKey: ['tpo-promo-library-data', `${queryKeyPrefix}-table-data`],
                            });
                            setOpenResetModal(false);
                        },
                        onError: (error: any) => {
                            const errorMsg = generateErrorMsg(
                                String(error?.response?.data?.detail || ''),
                                ErrorMessages.GENERALIZED_ERROR_MSG,
                            );
                            sendNotification('error', errorMsg);
                        },
                    });
                }}
                loading={resetPromoLibraryLoading}
                title={`Reset ${resetLabel}`}
                kpiOptions={kpiOptionsForReset}
            />
            <ValidateLibraryDrawer
                open={openValidateDrawer}
                onClose={() => setOpenValidateDrawer(false)}
            />
        </div>
    );
};

export default PromoLibraryContainer;
